import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, ArrowRight, BarChart3, Users, Sparkles, Globe, Navigation } from 'lucide-react';
export function Hero() {
  const scrollToSection = (id: string) => {
    const element = document.querySelector(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <section id="home" className="relative min-h-screen flex items-center pt-20 overflow-hidden bg-[#0a0a0a]" data-id="element-141">
      {/* Enhanced Background Pattern */}
      <div className="absolute inset-0 z-0 opacity-[0.1] pointer-events-none" data-id="element-142">
        <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)',
        backgroundSize: '40px 40px'
      }} data-id="element-143" />
      </div>

      {/* Animated gradient orbs - Warm Propulso tones */}
      <motion.div animate={{
      scale: [1, 1.2, 1],
      opacity: [0.15, 0.25, 0.15]
    }} transition={{
      duration: 8,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#ff8a33]/30 rounded-full blur-[100px]" data-id="element-144" />
      <motion.div animate={{
      scale: [1, 1.3, 1],
      opacity: [0.1, 0.2, 0.1]
    }} transition={{
      duration: 10,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 2
    }} className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-[100px]" data-id="element-145" />

      {/* Floating Elements - Minimal line icons */}
      <motion.div animate={{
      y: [0, -30, 0],
      rotate: [0, 5, 0]
    }} transition={{
      duration: 6,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="absolute top-1/4 right-[10%] z-0 text-white/5 hidden lg:block" data-id="element-146">
        <Globe size={180} strokeWidth={1} data-id="element-147" />
      </motion.div>
      <motion.div animate={{
      y: [0, 25, 0],
      rotate: [0, -5, 0]
    }} transition={{
      duration: 7,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 1
    }} className="absolute bottom-1/4 left-[5%] z-0 text-white/5 hidden lg:block" data-id="element-148">
        <Navigation size={160} strokeWidth={1} data-id="element-149" />
      </motion.div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-150">
        <div className="max-w-4xl mx-auto text-center" data-id="element-151">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-[#ff8a33] text-sm font-medium mb-8 shadow-sm backdrop-blur-sm" data-id="element-152">
            <motion.span animate={{
            rotate: [0, 360]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'linear'
          }} className="relative flex h-2 w-2" data-id="element-153">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#ff8a33] opacity-75" data-id="element-154"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#ff8a33]" data-id="element-155"></span>
            </motion.span>
            Accepting Early Partners
            <Sparkles size={14} className="text-[#ff8a33]" data-id="element-156" />
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="text-5xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight mb-8 leading-[1.1]" data-id="element-157">
            Turn Your Business Into a{' '}
            <span className="text-gradient-orange" data-id="element-158">
              Predictable Revenue Engine
            </span>
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="text-xl md:text-2xl text-gray-400 mb-8 leading-relaxed max-w-3xl mx-auto" data-id="element-159">
            I help location-dependent businesses use location intelligence,
            modern funnels, review systems, and smart automation to attract
            ideal customers, outperform nearby competitors, and maximize revenue
            — whether you operate a single site or multiple locations.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 mb-10 max-w-2xl mx-auto shadow-xl" data-id="element-160">
            <p className="text-sm md:text-base text-gray-300 leading-relaxed" data-id="element-161">
              <span className="font-bold text-[#ff8a33]" data-id="element-162">
                Why work with me?
              </span>{' '}
              I'm new to taking on clients publicly, which means early partners
              get intense senior-level execution and personal attention—without
              agency overhead or hidden juniors doing the real work.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-163">
            <motion.button onClick={() => scrollToSection('#book-call')} whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} className="group w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-[#ff8a33] to-yellow-500 text-white text-lg font-semibold rounded-xl transition-all shadow-lg shadow-orange-500/25 hover:shadow-xl hover:shadow-orange-500/40 flex items-center justify-center gap-2 relative overflow-hidden" data-id="element-164">
              <span className="relative z-10" data-id="element-165">Book a Strategy Call</span>
              <ArrowRight size={20} className="relative z-10 group-hover:translate-x-1 transition-transform" data-id="element-166" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#ff9f55] to-yellow-400 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-167" />
            </motion.button>
            <motion.button onClick={() => scrollToSection('#how-i-help')} whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} className="w-full sm:w-auto px-8 py-4 bg-white/5 text-white border border-white/10 text-lg font-semibold rounded-xl hover:bg-white/10 transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-2 backdrop-blur-sm" data-id="element-168">
              How I Help Businesses Grow
            </motion.button>
          </motion.div>

          {/* Enhanced Trust Indicators */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 1,
          delay: 0.8
        }} className="mt-20 pt-8 border-t border-white/10" data-id="element-169">
            <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-6" data-id="element-170">
              Trusted Expertise In
            </p>
            <div className="flex flex-wrap justify-center gap-8 md:gap-16" data-id="element-171">
              <motion.div whileHover={{
              y: -2
            }} className="flex items-center gap-3 text-gray-400" data-id="element-172">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center" data-id="element-173">
                  <MapPin className="w-5 h-5 text-blue-500" data-id="element-174" />
                </div>
                <span className="font-semibold" data-id="element-175">Location Intelligence</span>
              </motion.div>
              <motion.div whileHover={{
              y: -2
            }} className="flex items-center gap-3 text-gray-400" data-id="element-176">
                <div className="w-10 h-10 rounded-lg bg-[#ff8a33]/10 border border-[#ff8a33]/20 flex items-center justify-center" data-id="element-177">
                  <Users className="w-5 h-5 text-[#ff8a33]" data-id="element-178" />
                </div>
                <span className="font-semibold" data-id="element-179">
                  Demand Capture & Conversion
                </span>
              </motion.div>
              <motion.div whileHover={{
              y: -2
            }} className="flex items-center gap-3 text-gray-400" data-id="element-180">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center" data-id="element-181">
                  <BarChart3 className="w-5 h-5 text-emerald-500" data-id="element-182" />
                </div>
                <span className="font-semibold" data-id="element-183">
                  Revenue Systems & Automation
                </span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}